import { Children, isValidElement, useMemo } from 'react'
import { isDefined } from '../types'

/**
 * Use this hook to derive an aria-label.
 * Best way to use this is to pass the `props` of the component into the hook.
 * The props should include `children`, `aria-label`, and `aria-labelledby` if applicable.
 * @param props
 * @returns
 */
export function useDerivedAriaLabel(props: unknown): string | undefined {
  return useMemo(() => {
    const label = getAriaLabel(props)
    // Filter out empty strings
    if (label) return label
  }, [props])
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function getAriaLabel(arg: unknown): string | undefined {
  if (typeof arg === 'undefined') return
  if (arg === null) return
  if (typeof arg === 'string') return arg.trim()
  if (typeof arg === 'number') return arg.toString()
  if (typeof arg === 'boolean') return arg.toString()
  if (typeof arg === 'symbol') return arg.toString()
  if (typeof arg === 'bigint') return arg.toString()
  if (typeof arg === 'object' && !Array.isArray(arg)) {
    if ('aria-label' in arg) return `${arg['aria-label']}`
    if ('aria-labelledby' in arg) return
    if ('children' in arg) return getAriaLabel(arg['children'])
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (isValidElement<any>(arg)) {
    const children = arg
    if (Children.count(children) > 1) {
      const arr = Children.map(children, getAriaLabel)
      if (!arr) return
      const defined = arr.filter(isDefined)
      return defined.join(' ').trim()
    }

    if (children.props) {
      if (children.props['aria-label']) return children.props['aria-label']
      if (children.props['aria-labelledby']) return
      if (children.props.children) return getAriaLabel(children.props.children)
    }
  }
  if (Array.isArray(arg)) {
    const arr = arg.map(getAriaLabel)
    const defined = arr.filter(isDefined)
    return defined.join(' ').trim()
  }

  return
}
